import React, { useEffect, useMemo, useState } from "react";
import rawAxios from "axios";
import apiAxios from "../api/axios";
import "./ApplicationViewDeputy2.css";

const API_URL = process.env.REACT_APP_API_URL || "https://expertise.gov.kz";

const L = {
  kz: {
    title: "Өтініш туралы ақпарат",
    common: "Жалпы мәлімет",
    params: "Параметрлер",
    files: "Файлдар",

    appTitle: "Атауы",
    status: "Күйі",
    authors: "Автор(лар)",
    updated: "Жаңартылған күні",

    subject: "Пән",
    grade: "Сынып/жас",
    category: "Санат",
    educationLevel: "Білім деңгейі",
    direction: "Бағдар",
    criteriaForm: "Форма",
    format: "Формат",
    publicationType: "Басылым түрі",
    serviceType: "Қызмет түрі",
    teachingLang: "Оқыту тілі",
    devLang: "Әзірлеу тілі",
    pageCount: "Бет саны",
    sheetCount: "Парақ саны",
    duration: "Ұзақтығы",
    yearMonth: "Жыл/Ай",

    open: "Ашу",
    download: "Жүктеу",
    link: "Сілтеме",
    section: "Бөлім",
    noFiles: "Файлдар жоқ",
    loading: "Жүктелуде...",
    notFound: "Өтініш табылмады",
    close: "Жабу",

    openError: "Өтінішті ашу кезінде қате орын алды",
    fileOpenError: "Файлды ашу мүмкін болмады",
    fileDownloadError: "Файлды жүктеу мүмкін болмады",
    fileMissing: "Файл немесе сілтеме жоқ",
  },

  ru: {
    title: "Информация по заявке",
    common: "Общие сведения",
    params: "Параметры",
    files: "Файлы",

    appTitle: "Название",
    status: "Статус",
    authors: "Автор(ы)",
    updated: "Обновлено",

    subject: "Предмет",
    grade: "Класс/возраст",
    category: "Категория",
    educationLevel: "Уровень образования",
    direction: "Направление",
    criteriaForm: "Форма",
    format: "Формат",
    publicationType: "Тип издания",
    serviceType: "Тип услуги",
    teachingLang: "Язык обучения",
    devLang: "Язык разработки",
    pageCount: "Кол-во страниц",
    sheetCount: "Кол-во листов",
    duration: "Длительность",
    yearMonth: "Год/Месяц",

    open: "Открыть",
    download: "Скачать",
    link: "Ссылка",
    section: "Раздел",
    noFiles: "Файлов нет",
    loading: "Загрузка...",
    notFound: "Заявка не найдена",
    close: "Закрыть",

    openError: "Ошибка при открытии заявки",
    fileOpenError: "Не удалось открыть файл",
    fileDownloadError: "Не удалось скачать файл",
    fileMissing: "Нет файла или ссылки",
  },
};

const getLocale = () => {
  const locale = localStorage.getItem("locale") || "kz";
  return locale === "ru" ? "ru" : "kz";
};

const isEmpty = (value) => {
  return value === null || value === undefined || value === "";
};

const pickName = (value, locale) => {
  if (isEmpty(value)) return "—";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const result = value
      .map((item) => pickName(item, locale))
      .filter((item) => item && item !== "—")
      .join(", ");

    return result || "—";
  }

  return (
    value[`name_${locale}`] ||
    value.name ||
    value.title ||
    value.label ||
    value.code ||
    value.id ||
    "—"
  );
};

const getValue = (source, keys) => {
  if (!source) return null;

  for (const key of keys) {
    const value = source[key];

    if (!isEmpty(value)) {
      return value;
    }
  }

  return null;
};

const fmtDate = (value) => {
  if (!value) return "—";

  const date = new Date(String(value));

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("kk-KZ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const joinUrl = (baseUrl, path) => {
  if (!path) return "";

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = String(baseUrl).replace(/\/+$/, "");
  let cleanPath = String(path).replace(/^\/+/, "");

  if (base.endsWith("/api") && cleanPath.startsWith("api/")) {
    cleanPath = cleanPath.slice(4);
  }

  return `${base}/${cleanPath}`;
};

const getFileDownloadUrl = (file) => {
  // если is_link — download_url это внешняя ссылка, не PDF, игнорируем
  if (file?.is_link) return "";

  const raw =
    file?.download_url ||
    file?.downloadUrl ||
    file?.file_url ||
    file?.fileUrl ||
    file?.url ||
    "";

  return raw ? joinUrl(API_URL, raw) : "";
};

const getFileLinkUrl = (file) => {
  return (
    file?.link_url ||
    file?.external_url ||
    file?.file_link ||
    file?.link ||
    ""
  );
};

const isPresignedMinioUrl = (url) => {
  if (!url) return false;

  const lower = String(url).toLowerCase();

  return (
    lower.includes("x-amz-signature=") ||
    lower.includes("x-amz-algorithm=") ||
    lower.includes("x-amz-credential=") ||
    lower.includes("minio.expertise.gov.kz")
  );
};

const openExternal = (url) => {
  if (!url) return;

  const opened = window.open(url, "_blank", "noopener,noreferrer");

  if (!opened) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

const getFilenameFromDisposition = (disposition) => {
  if (!disposition) return null;

  const utf = /filename\*=([^']*)''([^;]+)/i.exec(disposition);

  if (utf?.[2]) {
    return decodeURIComponent(utf[2]);
  }

  const ascii = /filename="?([^";]+)"?/i.exec(disposition);

  return ascii?.[1] || null;
};

const safeFilename = (value) => {
  return String(value || "file")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .toLowerCase();
};

const buildFallbackFilename = (application, file, index) => {
  const appId = application?.id || "application";
  const section = file?.section || index + 1;

  // если есть оригинальное имя архива — используем его
  const archiveName = file?.section_archive_name || file?.section_archive?.original_name;
  if (archiveName) return archiveName;

  // обычный PDF файл
  return `application_${safeFilename(appId)}_section_${safeFilename(section)}.pdf`;
};

export default function ApplicationViewDeputy2({ applicationId, onClose }) {
  const locale = useMemo(() => getLocale(), []);
  const t = useMemo(() => L[locale] || L.kz, [locale]);

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [busy, setBusy] = useState({});
  // busy example:
  // {
  //   "123:open": { pct: null },
  //   "123:download": { pct: 40 }
  // }

  const token = localStorage.getItem("token");

  const authConfig = useMemo(() => {
    if (!token) return {};

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [token]);

  useEffect(() => {
    if (!applicationId) return;

    let active = true;

    const fetchApplication = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await apiAxios.get(
          `/backoffice/applications/${applicationId}`,
          authConfig
        );

        const payload = res?.data ?? {};
        const base = payload.application ?? payload.data ?? payload;

        const normalized = {
          ...base,
          files: payload.files ?? base.files ?? [],
          status: payload.status ?? base.status,
        };

        if (active) {
  setApplication(normalized);
}
      } catch (e) {

        if (active) {
          setError(t.openError);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchApplication();

    return () => {
      active = false;
    };
  }, [applicationId, authConfig, t.openError]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  const setBusyState = (key, pct) => {
    setBusy((prev) => ({
      ...prev,
      [key]: { pct },
    }));
  };

  const clearBusyState = (key) => {
    setBusy((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const loadBlob = async (file, key) => {
    const downloadUrl = !file.is_link ? getFileDownloadUrl(file) : null;
    const isMinio = isPresignedMinioUrl(downloadUrl);

    const config = {
      responseType: "blob",
      onDownloadProgress: (event) => {
        const total = event?.total || 0;
        const loaded = event?.loaded || 0;

        if (total > 0) {
          const pct = Math.max(
            0,
            Math.min(100, Math.round((loaded * 100) / total))
          );

          setBusyState(key, pct);
        } else {
          setBusyState(key, null);
        }
      },
    };

    /**
     * МАҢЫЗДЫ:
     * MinIO signed URL ішінде X-Amz-Signature бар.
     * Оған Authorization: Bearer жіберуге болмайды.
     *
     * Сондықтан:
     * - MinIO signed URL => rawAxios, headers жоқ
     * - Backend API URL => apiAxios, Bearer token бар
     */
    if (isMinio) {
      return rawAxios.get(downloadUrl, config);
    }

    if (token) {
      config.headers = {
        Authorization: `Bearer ${token}`,
      };
    }

    return apiAxios.get(downloadUrl, config);
  };

const handleOpenFile = async (file, index) => {
  const downloadUrl = file.is_link ? "" : getFileDownloadUrl(file);
    const linkUrl = getFileLinkUrl(file);

    if (!downloadUrl && linkUrl) {
      openExternal(linkUrl);
      return;
    }

    if (!downloadUrl) {
      alert(t.fileMissing);
      return;
    }

    /**
     * Егер бұл MinIO signed URL болса,
     * PDF-ті тура жаңа вкладкада ашамыз.
     * Осылай Bearer token мүлде жіберілмейді.
     */
    if (isPresignedMinioUrl(downloadUrl)) {
      openExternal(downloadUrl);
      return;
    }

    const fileKey = file?.id ?? index;
    const key = `${fileKey}:open`;

    try {
      setBusyState(key, null);

      const res = await loadBlob(file, key);
      const contentType = res?.data?.type || "application/pdf";
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      openExternal(blobUrl);

      /**
       * Мұнда URL.revokeObjectURL жасамаймыз.
       * Себебі PDF ұзақ ашық тұруы мүмкін.
       */
    } catch (e) {
      alert(t.fileOpenError);
    } finally {
      clearBusyState(key);
    }
  };

const handleDownloadFile = async (file, index) => {
  const downloadUrl = file.is_link
    ? (file.section_archive_download_url || file.section_archive?.download_url || "")
    : getFileDownloadUrl(file);
    if (!downloadUrl) {
      alert(t.fileMissing);
      return;
    }

    const fileKey = file?.id ?? index;
    const key = `${fileKey}:download`;

    /**
     * MinIO signed URL үшін алдымен rawAxios арқылы blob жүктейміз.
     * Егер CORS кедергі жасаса, fallback ретінде тура ашамыз.
     */
    if (isPresignedMinioUrl(downloadUrl)) {
      try {
        setBusyState(key, 0);

        const res = await rawAxios.get(downloadUrl, {
          responseType: "blob",
          onDownloadProgress: (event) => {
            const total = event?.total || 0;
            const loaded = event?.loaded || 0;

            if (total > 0) {
              const pct = Math.max(
                0,
                Math.min(100, Math.round((loaded * 100) / total))
              );

              setBusyState(key, pct);
            } else {
              setBusyState(key, null);
            }
          },
        });

        const disposition =
          res.headers?.["content-disposition"] ||
          res.headers?.get?.("content-disposition");

        const suggestedName = getFilenameFromDisposition(disposition);
        const filename =
          suggestedName || buildFallbackFilename(application, file, index);

        const blobUrl = window.URL.createObjectURL(res.data);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.download = buildFallbackFilename(application, file, index);
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        clearBusyState(key);
      }

      return;
    }

    try {
      setBusyState(key, 0);

      const res = await loadBlob(file, key);

      const disposition =
        res.headers?.["content-disposition"] ||
        res.headers?.get?.("content-disposition");

      const suggestedName = getFilenameFromDisposition(disposition);
      const filename =
        suggestedName || buildFallbackFilename(application, file, index);

      const blobUrl = window.URL.createObjectURL(res.data);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(t.fileDownloadError);
    } finally {
      clearBusyState(key);
    }
  };

  const statusValue = pickName(getValue(application, ["status"]), locale);

const commonRows = [
  {
    label: t.status,
    value: statusValue,
    type: "status",
  },
];


  const year = pickName(
    getValue(application, ["submission_year", "submissionYear"]),
    locale
  );

  const month = pickName(
    getValue(application, ["submission_month", "submissionMonth"]),
    locale
  );

  const yearMonth =
    year !== "—" || month !== "—"
      ? `${year !== "—" ? year : ""}${
          year !== "—" && month !== "—" ? " / " : ""
        }${month !== "—" ? month : ""}`
      : "—";

const paramRows = [
  {
    label: t.category,
    value: pickName(getValue(application, ["category"]), locale),
  },
  {
    label: t.criteriaForm,
    value: pickName(getValue(application, ["criteria_form", "criteriaForm"]), locale),
  },
  {
    label: t.pageCount,
    value: pickName(getValue(application, ["page_count", "pageCount"]), locale),
  },
  {
    label: t.sheetCount,
    value: pickName(getValue(application, ["sheet_count", "sheetCount"]), locale),
  },
  {
    label: t.duration,
    value: pickName(getValue(application, ["duration_minutes", "durationMinutes"]), locale),
  },
];

  if (loading) {
    return (
      <div className="av2-overlay">
        <div className="av2-modal">
          <p className="av2-loading">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="av2-overlay" onClick={onClose}>
        <div className="av2-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="av2-close" onClick={onClose}>
            ×
          </button>

          <p className="av2-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="av2-overlay" onClick={onClose}>
        <div className="av2-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="av2-close" onClick={onClose}>
            ×
          </button>

          <p className="av2-empty">{t.notFound}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="av2-overlay" onClick={onClose}>
      <div className="av2-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="av2-close" onClick={onClose}>
          ×
        </button>

        <div className="av2-header">
          <h2>{t.title}</h2>
        </div>

        <div className="av2-body">
          <section className="av2-block">
            <h3>{t.common}</h3>

            <div className="av2-grid">
              {commonRows.map((row) => (
                <React.Fragment key={row.label}>
                  <div className="av2-label">{row.label}</div>

                  <div className="av2-value">
                    {row.type === "status" ? (
                      <span className="av2-status-badge">{row.value}</span>
                    ) : (
                      row.value
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </section>

          <section className="av2-block">
            <h3>{t.params}</h3>

            <div className="av2-grid">
              {paramRows.map((row) => (
                <React.Fragment key={row.label}>
                  <div className="av2-label">{row.label}</div>
                  <div className="av2-value">{row.value}</div>
                </React.Fragment>
              ))}
            </div>
          </section>

          {application?.word && (
  <section className="av2-block">
    <h3>{locale === "kz" ? "Доработка файлы" : "Файл доработки"}</h3>
    <div className="av2-file-list">
      <div className="av2-file-row">
        <div className="av2-file-main">
          <div className="av2-file-icon">📝</div>
          <div className="av2-file-meta">
            <div className="av2-file-name">
              {application.word?.original_name || "document.docx"}
            </div>
          </div>
        </div>
        <div className="av2-file-actions">
          <button
            type="button"
            className="av2-btn av2-btn-primary"
            onClick={() => {
              const wordName = application.word?.original_name || "document.docx";
              fetch(`${API_URL}/api/applications/${application.id}/download-word`, {
                headers: { Authorization: `Bearer ${token}` }
              })
                .then(r => r.blob())
                .then(blob => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = wordName;
                  a.click();
                  URL.revokeObjectURL(a.href);
                })
                .catch(() => alert(locale === "kz" ? "❌ Жүктеу мүмкін болмады" : "❌ Не удалось скачать"));
            }}
          >
            {locale === "kz" ? "Жүктеу" : "Скачать"}
          </button>
        </div>
      </div>
    </div>
  </section>
)}

          <section className="av2-block">
            <h3>{t.files}</h3>

            {application.files?.length ? (
              <ul className="av2-file-list">
                {application.files.map((file, index) => {
                  const fileKey = file?.id ?? index;

                  const openKey = `${fileKey}:open`;
                  const downloadKey = `${fileKey}:download`;

                  const opening = !!busy[openKey];
                  const downloading = !!busy[downloadKey];

                  const activeBusy = busy[openKey] || busy[downloadKey];
                  const pct = activeBusy?.pct;

const downloadUrl = !file.is_link ? getFileDownloadUrl(file) : null;
const linkUrl = getFileLinkUrl(file);

const hasDownload = !!downloadUrl;
const hasLink = !!linkUrl;  // показываем независимо от hasDownload

// + добавить архив секции
const sectionArchiveUrl = file?.section_archive_download_url || file?.section_archive?.download_url || null;
const hasArchive = !!sectionArchiveUrl;

                  const section = file?.section || index + 1;

                  return (
                    <li key={fileKey} className="av2-file-row">
                      <div className="av2-file-main">
                        <div className="av2-file-icon">📄</div>

                        <div className="av2-file-meta">
                          <div className="av2-file-name">
                            {t.section} {section}
                          </div>

                          <div className="av2-file-sub">
  {[
    hasDownload && "PDF",
    hasLink && (locale === "kz" ? "Сілтеме" : "Ссылка"),
    hasArchive && (locale === "kz" ? "Архив бар" : "Есть архив"),
  ]
    .filter(Boolean)
    .join(" · ")}
</div>
                        </div>
                      </div>

                      <div className="av2-file-actions">
  {file.is_link ? (
    <>
      {hasLink && (
        <button
          type="button"
          className="av2-btn av2-btn-secondary"
          onClick={() => openExternal(linkUrl)}
        >
          {t.link}
        </button>
      )}

      {hasArchive && (
        <button
          type="button"
          className="av2-btn av2-btn-primary"
          onClick={() => handleDownloadFile({ ...file, download_url: file.section_archive.download_url, is_link: false }, index)}
          disabled={downloading}
        >
          {downloading
            ? `${locale === "kz" ? "Архив жүктеу" : "Скачать архив"}${typeof pct === "number" ? ` ${pct}%` : "..."}`
            : locale === "kz" ? "Архив жүктеу" : "Скачать архив"}
        </button>
      )}
    </>
  ) : (
    <>
      {hasDownload && (
        <>
          <button
            type="button"
            className="av2-btn av2-btn-secondary"
            onClick={() => handleOpenFile(file, index)}
            disabled={opening || downloading}
          >
            {opening ? `${t.open}...` : t.open}
          </button>

          <button
            type="button"
            className="av2-btn av2-btn-primary"
            onClick={() => handleDownloadFile(file, index)}
            disabled={opening || downloading}
          >
            {downloading
              ? `${t.download}${typeof pct === "number" ? ` ${pct}%` : "..."}`
              : t.download}
          </button>
        </>
      )}
    </>
  )}
</div>

                      {(opening || downloading) && (
                        <div
                          className={`av2-progress ${
                            typeof pct !== "number" ? "is-indeterminate" : ""
                          }`}
                        >
                          <div
                            className="av2-progress-fill"
                            style={{
                              width: typeof pct === "number" ? `${pct}%` : "40%",
                            }}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="av2-muted">{t.noFiles}</p>
            )}
          </section>
        </div>

        <div className="av2-footer">
          <button
            type="button"
            className="av2-btn av2-btn-ghost"
            onClick={onClose}
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}